import { DataResponse, RequestStatus } from "@imagine/frontend-base";
import { Preferences, StartedTour, User } from "@imagine/shared-base";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { UserThunks } from "./UserThunks";

interface UserState {
  user: User | null;
  preferences: Preferences;
  tours: StartedTour[];
  status: RequestStatus;
  usedFeatures: number[];
  apiVersion: string;
}

// All data related to the user
const initialState: UserState = {
  user: null,
  preferences: {},
  tours: [],
  status: RequestStatus.IDLE,
  usedFeatures: [],
  apiVersion: "",
};

export const userSlice = createSlice({
  name: "user",
  initialState: initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
    },
    setPreferences: (state, action: PayloadAction<Preferences>) => {
      state.preferences = action.payload;
    },
    setTours: (state, action: PayloadAction<StartedTour[]>) => {
      state.tours = action.payload;
    },
    track: (state, action: PayloadAction<number>) => {
      // Only track actions for logged in users
      if (state.user !== null && !state.usedFeatures.includes(action.payload)) {
        state.usedFeatures.push(action.payload);
      }
    },
    clearUser: (state) => {
      state.user = null;
      state.status = RequestStatus.IDLE;
    },
    clearPreferences: (state) => {
      state.preferences = {};
    },
    setApiVersion: (state, action: PayloadAction<string>) => {
      state.apiVersion = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(UserThunks.fetchLoggedInUser.pending, (state) => {
        state.status = RequestStatus.LOADING;
      })
      .addCase(
        UserThunks.fetchLoggedInUser.fulfilled,
        (
          state,
          action: PayloadAction<
            DataResponse<{
              user: User;
              preferences: Preferences;
              tours: StartedTour[];
            }>
          >,
        ) => {
          if (action.payload.apiVersion) {
            state.apiVersion = action.payload.apiVersion;
          }
          if (action.payload.success && action.payload.data) {
            // When in strict mode, this is called twice. Make sure second call does no override default data
            if (
              action.payload.data.preferences &&
              Object.keys(action.payload.data.preferences).length > 0
            ) {
              state.preferences = action.payload.data.preferences;
            }
            if (action.payload.data.tours) {
              state.tours = action.payload.data.tours;
            }
            state.user = action.payload.data.user;
          }
          state.status = RequestStatus.READY;
          console.log("User state updated", state.status);
        },
      )
      .addCase(UserThunks.fetchLoggedInUser.rejected, (state) => {
        console.log("Failed to fetch logged in user");
        state.status = RequestStatus.FAILED;
      })
      .addCase(UserThunks.loginUser.fulfilled, (state, action) => {
        console.log("Login user response", action.payload);
        if (action.payload.success && action.payload.data) {
          // When in strict mode, this is called twice. Make sure second call does no override default data
          if (
            action.payload.data.preferences &&
            Object.keys(action.payload.data.preferences).length > 0
          ) {
            state.preferences = action.payload.data.preferences;
          }
          if (action.payload.data.tours) {
            state.tours = action.payload.data.tours;
          }
          state.user = action.payload.data.user;
        }
        state.status = RequestStatus.READY;
        console.log("User state updated", state.status);
      });
  },
});

export const UserActions = userSlice.actions;

export const userReducer = userSlice.reducer;
